import { FunctionComponent, ReactNode } from "react";

import styled from "styled-components/macro";

import { EntityKind } from "models/entityKind";

import SegmentedToggle, { SegmentedVariant } from "components/shared/SegmentedToggle";

import MonthlyFieldSelector from "./MonthlyFieldSelector";
import { fadeInOnSwitch, useSwitched } from "./monthlyFade";

export const TOOLBAR_GAP = 8;
// Fixed, so the toolbar's overflow measurement can reserve it.
export const VIEW_SWITCH_WIDTH = 184;

const VIEW_OPTIONS = [
  { label: "Well Data", value: "data" },
  { label: "Monthly", value: "monthly" }
];

/** What the toolbar's lead column holds besides the field selector, for the overflow measurement. */
export const toolbarLeadingWidth = (entityKind: EntityKind) =>
  entityKind === EntityKind.Well ? VIEW_SWITCH_WIDTH + TOOLBAR_GAP : 0;

interface MonthlyToolbarLeadProps {
  entityKind: EntityKind;
  monthly: boolean;
  onToggle: () => void;
  // The Data view's column chooser, which the read-only monthly selector stands in for.
  chooser: ReactNode;
}

// The view switch, first in the toolbar for wells, then the field selector for the view.
const MonthlyToolbarLead: FunctionComponent<MonthlyToolbarLeadProps> = ({
  entityKind,
  monthly,
  onToggle,
  chooser
}) => {
  const switched = useSwitched(monthly);

  return (
    <ToolbarLead>
      {entityKind === EntityKind.Well && (
        <SegmentedToggle
          variant={SegmentedVariant.Base}
          block
          aria-label="Data Table view"
          style={{ width: VIEW_SWITCH_WIDTH, flexShrink: 0 }}
          options={VIEW_OPTIONS}
          value={monthly ? "monthly" : "data"}
          onChange={onToggle}
        />
      )}
      <LeadBody key={monthly ? "monthly" : "data"} $fade={switched}>
        {monthly ? <MonthlyFieldSelector /> : chooser}
      </LeadBody>
    </ToolbarLead>
  );
};

export default MonthlyToolbarLead;

const ToolbarLead = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${TOOLBAR_GAP}px;
`;

const LeadBody = styled.div<{ $fade: boolean }>`
  ${(p) => p.$fade && fadeInOnSwitch}
  flex: 1 1 auto;
  min-width: 0;
  display: grid;
`;
